import { useEffect, useState } from "react";
import {
  investors as investorsApi,
  flowRun,
  type Investor,
  type QpFlowOption,
  type FlowRun,
} from "../api";
import { qpRail } from "../lib/qpRail";
import { PhoneFrame } from "./investorApp/PhoneFrame";
import { FlowRunner } from "./investorApp/FlowRunner";

// The "Investor App" area. The back office launches a self-service advice
// session for an investor; everything inside the phone is the end-investor's
// retail experience, driven over the Quantfolio API. See
// docs/quantfolio-mobile-flow.md.
export function InvestorApp() {
  const [run, setRun] = useState<FlowRun | null>(null);

  if (run) {
    // Compact chrome so the phone gets the full height and never looks short.
    return (
      <div className="page iapp-page">
        <div className="iapp-topbar">
          <span className="eyebrow">Investor App — live over the Quantfolio API</span>
          <div className="iapp-topbar-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => qpRail.toggle()}>
              QP activity
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRun(null)}>
              Close app
            </button>
          </div>
        </div>
        <div className="iapp-stage">
          <PhoneFrame>
            <FlowRunner run={run} onExit={() => setRun(null)} />
          </PhoneFrame>
        </div>
      </div>
    );
  }

  return <Launcher onLaunched={setRun} />;
}

function Launcher({ onLaunched }: { onLaunched: (run: FlowRun) => void }) {
  const [investors, setInvestors] = useState<Investor[] | null>(null);
  const [flows, setFlows] = useState<QpFlowOption[] | null>(null);
  const [recent, setRecent] = useState<FlowRun[]>([]);
  const [investorId, setInvestorId] = useState<number | "">("");
  const [flowId, setFlowId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    investorsApi.list().then((d) => setInvestors(d.investors)).catch((e) => setErr(e.message));
    flowRun.flows().then((d) => setFlows(d.flows)).catch((e) => setErr(e.message));
    flowRun.runs().then((d) => setRecent(d.runs)).catch(() => {});
  }, []);

  // Default to the tenant's Investment Advice flow when it loads.
  useEffect(() => {
    if (flows && !flowId) {
      const adv = flows.find((f) => /investment advice/i.test(f.name)) ?? flows[0];
      if (adv) setFlowId(adv.id);
    }
  }, [flows, flowId]);

  async function launch() {
    if (!investorId || !flowId) return;
    setBusy(true);
    setErr(null);
    try {
      const flow = flows?.find((f) => f.id === flowId);
      const { run } = await flowRun.start(Number(investorId), flowId, flow?.name);
      onLaunched(run);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  async function resume(id: number) {
    try {
      const { run } = await flowRun.run(id);
      onLaunched(run);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Investor App</span>
          <h1>Launch self-service advice</h1>
        </div>
      </header>
      <p className="muted">
        Start the Andersen mobile app for an investor. They'll answer their own
        suitability questions and receive a real Quantfolio investment
        recommendation — the whole flow runs over the QP API, no advisor needed.
      </p>

      {err && <div className="banner-error">{err}</div>}

      <div className="card" style={{ maxWidth: "34rem" }}>
        <div className="card-form">
          <label className="field">
            <span>Investor *</span>
            <select
              value={investorId}
              onChange={(e) => setInvestorId(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Select investor…</option>
              {(investors ?? []).map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                  {i.qp_client_id ? " · linked" : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span>Flow</span>
            <p className="muted" style={{ margin: 0 }}>
              {(flows ?? []).find((f) => f.id === flowId)?.name ?? "Investment Advice"}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={launch}
            disabled={!investorId || !flowId || busy}
          >
            {busy ? "Launching…" : "Launch app →"}
          </button>
        </div>
      </div>

      {recent.length > 0 && (
        <>
          <h2 className="section-title" style={{ marginTop: "var(--s-xl)" }}>
            Recent sessions
          </h2>
          <table className="table">
            <thead>
              <tr>
                <th>Flow</th>
                <th>Status</th>
                <th>Started</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} className="row-link" onClick={() => resume(r.id)}>
                  <td className="strong">{r.flow_name ?? "—"}</td>
                  <td>
                    <span className={`pill status-${r.status}`}>{r.status.replace("_", " ")}</span>
                  </td>
                  <td>{new Date(r.created_at).toLocaleDateString("en-GB")}</td>
                  <td className="muted">Resume →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
